import type { AppServices } from '@dcc/rules/app/services.ts';
import type { Roller } from '@dcc/rules/rules/dice.ts';
import { randomRoller } from '@dcc/rules/rules/dice.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { describeElapsed } from '@dcc/rules/rules/gameTime.ts';
import { activateItemPower } from '@dcc/rules/app/itemPowerService.ts';
import { dccCoreConditions } from '@dcc/rules/content/dccCoreConditions.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import type { ItemPowerStatusView, RoomInventory, SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { inventoryCatalogue } from './inventoryHotlist.ts';

/**
 * Activating an Item Power from a sheet.
 *
 * The rules engine works out what happens to the crawler; the table's server
 * spends the Item's charge and saves both in one transaction. If the server
 * refuses (no charge left, the sheet is stale) NOTHING was saved, and the dice
 * that were rolled are discarded with it.
 */
export type ItemPowerUse =
  | { ok: true; note: string; inventory: RoomInventory; /** Actions this costs in a fight, for the shared tracker. */ combatActions: 0 | 1 }
  | { ok: false; reason: string };

export async function runItemPower(
  storage: Pick<SheetsApiStorage, 'activateItem'>,
  services: AppServices,
  characterId: string,
  itemId: string,
  roller: Roller = randomRoller,
): Promise<ItemPowerUse> {
  const character = await services.repository.load(characterId);
  const runtime = await services.runtime.loadOrStart(characterId);
  const outcome = activateItemPower(character, runtime, itemId, {
    catalogue: inventoryCatalogue,
    conditions: dccCoreConditions,
    effects: activeEffects(runtime),
    roller,
    newId,
  });
  if (!outcome.ok) return outcome;

  // Only records that actually changed are sent, stamped exactly as an
  // ordinary save would stamp them.
  const { inventory } = await storage.activateItem(characterId, itemId, {
    ...(outcome.character === character ? {} : { character: services.repository.stamp(outcome.character) }),
    ...(outcome.runtime === runtime ? {} : { runtime: services.runtime.stamp(outcome.runtime) }),
  });
  const item = inventoryCatalogue.lookupItem(itemId);
  // Rules p.49: a Potion costs an Action in a fight whatever its definition says.
  const combatActions = item?.power && (item.power.actionCost === 1 || (item.kind === 'potion' && item.power.consumes)) ? 1 : 0;
  return { ok: true, note: outcome.lines.join(' '), inventory, combatActions };
}

/** "2/3 charges · next in 1h 30m" for a list row, or null when the Item keeps no state. */
export function describePowerStatus(status: ItemPowerStatusView | undefined): string | null {
  if (status === undefined) return null;
  const parts: string[] = [];
  if (status.charges !== null && status.maxCharges !== null) {
    parts.push(`${status.charges}/${status.maxCharges} charge${status.maxCharges === 1 ? '' : 's'}`);
    if (status.secondsToNextCharge !== null) parts.push(`next in ${describeElapsed(status.secondsToNextCharge)}`);
  }
  if (status.cooldownSecondsRemaining > 0) parts.push(`ready in ${describeElapsed(status.cooldownSecondsRemaining)}`);
  return parts.length > 0 ? parts.join(' · ') : 'Ready';
}
